import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import TextInput from "../../Common/FormElements/TextInput/TextInput";
import TextArea from "../../Common/FormElements/TextArea/TextArea";
import Button from "../../Common/FormElements/Button/Button";
import { updateProject } from "../../../REDUX/actions/appProjects";
import { uploadFile } from "../../../Utils";
import { IMAGE_END_POINT } from "../../../Api/EndPoints";

const TEAL = "#14748e";
const TILE = 84;
const MIN_SLOTS = 6;

/** "emplacement-1.jpg": numbered in the order the customer arranged them. */
const photoFileName = (prefix, photo, index) =>
  `${prefix}-${index + 1}${photo.url.slice(photo.url.lastIndexOf("."))}`;

const READINESS_FIELDS = [
  {
    name: "slab",
    label: "Slab",
    helper: "Specify whether a slab is planned for the spa.",
  },
  {
    name: "waterSupply",
    label: "Water supply",
    helper:
      "Check for a suitable water connection for supply and drainage.",
  },
  {
    name: "craning",
    label: "Craning",
    helper: "Specify whether craning is required for the spa delivery.",
  },
  {
    name: "waterDrainage",
    label: "Water drainage",
    helper: "Make sure water drainage is planned near the spa.",
  },
];

const RADIO_OPTIONS = [
  { code: "yes", label: "Yes" },
  { code: "no", label: "No" },
  { code: "planned", label: "Not yet" },
];

/** Section block: bold title, grey helper, bottom separator. */
const Section = ({ title, helper, children }) => {
  const { t } = useTranslation("common");
  return (
    <div className="pb-4 mb-4 border-bottom">
      <h4 className="f16 bold quickSand text-blue mb-1">{t(title)}</h4>
      {helper && <p className="f13 text-brightGrey mb-3">{t(helper)}</p>}
      {children}
    </div>
  );
};

/** Oui / Non / Pas encore radio row. */
const RadioRow = ({ name, value, onChange }) => {
  const { t } = useTranslation("common");
  return (
    <div className="d-flex align-items-center gap-4 mb-2">
      {RADIO_OPTIONS.map(({ code, label }) => (
        <label
          key={code}
          className="d-flex align-items-center gap-2 f14 text-ultraBlack pointer mb-0"
        >
          <input
            type="radio"
            name={name}
            checked={value === code}
            onChange={() => onChange(code)}
            style={{ accentColor: TEAL, width: 16, height: 16 }}
          />
          {t(label)}
        </label>
      ))}
    </div>
  );
};

/** Photo row: "+" tile, thumbnails, then empty slots up to MIN_SLOTS. */
const PhotoRow = ({ name, photos, onChange }) => {
  const { t } = useTranslation("common");
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (event) => {
    const files = [...(event.target.files ?? [])];
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const id = await uploadFile(file);
        uploaded.push({ id, url: URL.createObjectURL(file), local: true });
      }
      onChange([...photos, ...uploaded]);
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const emptySlots = Math.max(MIN_SLOTS - 1 - photos.length, 0);
  const tileStyle = {
    width: TILE,
    height: TILE,
    borderRadius: 8,
    border: `2px solid ${TEAL}`,
  };

  return (
    <div className="d-flex flex-wrap gap-3">
      <button
        type="button"
        className="d-flex align-items-center justify-content-center bg-white pointer"
        style={{ ...tileStyle, color: TEAL }}
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? (
          <span className="f13">…</span>
        ) : (
          <i className="bi bi-plus-lg f22" />
        )}
      </button>
      {photos.map((photo, index) => (
        <div key={photo.id} className="position-relative">
          <img
            src={photo.local ? photo.url : `${IMAGE_END_POINT}${photo.url}`}
            alt=""
            style={{ ...tileStyle, objectFit: "cover" }}
          />
          <button
            type="button"
            className="position-absolute top-0 end-0 border-0 br50 bg-white text-red-600 f12 d-flex align-items-center justify-content-center m-1"
            style={{ width: 20, height: 20 }}
            onClick={() =>
              onChange(photos.filter((_, photoIndex) => photoIndex !== index))
            }
          >
            <i className="bi bi-x-lg" />
          </button>
          {!photo.local && (
            <button
              type="button"
              className="position-absolute bottom-0 end-0 border-0 br50 bg-white f12 d-flex align-items-center justify-content-center m-1"
              style={{ width: 20, height: 20, color: TEAL }}
              title={t("Download")}
              onClick={() =>
                saveAs(
                  `${IMAGE_END_POINT}${photo.url}`,
                  photoFileName(name, photo, index)
                )
              }
            >
              <i className="bi bi-download" />
            </button>
          )}
        </div>
      ))}
      {[...Array(emptySlots)].map((_, index) => (
        <div key={index} className="bg-white" style={tileStyle} />
      ))}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={handleFiles}
      />
    </div>
  );
};

/**
 * Slide 8 — Info projet, laid out like the app's project form: sectioned
 * blocks with helper texts, Oui/Non/Pas encore radios, photo tile rows and
 * titled comment areas. Address is attached to the PROJECT.
 */
const ProjectInfo = ({ project }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const { updateLoading } = useSelector((state) => state.appProjectsReducer);
  const info = project.info ?? {};

  const [formData, setFormData] = useState({
    street: project.street ?? "",
    zip: project.zip ?? "",
    city: project.city ?? "",
    country: project.country ?? "",
    slab: info.slab ?? null,
    waterSupply: info.waterSupply ?? null,
    craning: info.craning ?? null,
    waterDrainage: info.waterDrainage ?? null,
    electricityComment: info.electricityComment ?? "",
    drainageComment: info.drainageComment ?? "",
    locationPhotos: info.locationPhotos ?? [],
    addressPhotos: info.addressPhotos ?? [],
  });
  const setField = (name, value) =>
    setFormData((prev) => ({ ...prev, [name]: value }));

  const [zipping, setZipping] = useState(false);
  const photoGroups = [
    ["emplacement", formData.locationPhotos],
    ["acces", formData.addressPhotos],
  ];
  const hasSavedPhotos = photoGroups.some(([, photos]) =>
    photos.some((photo) => !photo.local)
  );

  // Every saved photo of both rows in one zip (not yet saved = not on the server).
  const downloadAllPhotos = async () => {
    setZipping(true);
    try {
      const zip = new JSZip();
      for (const [prefix, photos] of photoGroups) {
        for (const [index, photo] of photos.entries()) {
          if (photo.local) continue;
          const response = await fetch(`${IMAGE_END_POINT}${photo.url}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          zip.file(photoFileName(prefix, photo, index), await response.blob());
        }
      }
      saveAs(
        await zip.generateAsync({ type: "blob" }),
        `photos-projet-${project.number ?? project.id}.zip`
      );
    } catch (error) {
      console.log(error);
      toast.error(t("An error has occurred!"));
    } finally {
      setZipping(false);
    }
  };

  const handleSave = () => {
    dispatch(
      updateProject(
        project.id,
        {
          street: formData.street,
          zip: formData.zip,
          city: formData.city,
          country: formData.country,
          info: {
            slab: formData.slab,
            waterSupply: formData.waterSupply,
            craning: formData.craning,
            waterDrainage: formData.waterDrainage,
            electricityComment: formData.electricityComment,
            drainageComment: formData.drainageComment,
            locationPhotos: formData.locationPhotos.map((photo) => photo.id),
            addressPhotos: formData.addressPhotos.map((photo) => photo.id),
          },
        },
        t
      )
    );
  };

  const addressField = (label, name) => (
    <div className="col-6" key={name}>
      <label className="f14 text-ultraBlack sBold mb-1">{t(label)}</label>
      <TextInput
        placeholder={t(label)}
        value={formData[name]}
        callback={(e) => setField(name, e.target.value)}
      />
    </div>
  );

  return (
    <div className="flex-1 d-flex flex-column">
      <div className="modalContent px-5 pt-4 pb-5">
        <Section
          title="Project address"
          helper="Project address attached to the project (not to the prospect account)."
        >
          <div className="row g-3">
            {addressField("Street", "street")}
            {addressField("Postal code", "zip")}
            {addressField("City", "city")}
            {addressField("Country", "country")}
          </div>
        </Section>

        <Section
          title="Location photos"
          helper="Image of the spa location (or local visit) to visualize the area on the map."
        >
          <PhotoRow
            name="emplacement"
            photos={formData.locationPhotos}
            onChange={(photos) => setField("locationPhotos", photos)}
          />
        </Section>

        <Section
          title="Address photos"
          helper="Photos of the project address."
        >
          <PhotoRow
            name="acces"
            photos={formData.addressPhotos}
            onChange={(photos) => setField("addressPhotos", photos)}
          />
        </Section>

        {hasSavedPhotos && (
          <div className="pb-4 mb-4 border-bottom">
            <Button
              variant="outline"
              loading={zipping}
              onClick={downloadAllPhotos}
            >
              {t("Download all photos")}
            </Button>
          </div>
        )}

        {READINESS_FIELDS.map(({ name, label, helper }) => (
          <Section key={name} title={label}>
            <RadioRow
              name={name}
              value={formData[name]}
              onChange={(code) => setField(name, code)}
            />
            <p className="f13 text-brightGrey mb-0">{t(helper)}</p>
          </Section>
        ))}

        <Section
          title="Electricity comment"
          helper="Indicate the technical connection details (water, electricity, etc.) required for the installation."
        >
          <TextArea
            placeholder={t("Your comment")}
            rows={3}
            value={formData.electricityComment}
            callback={(e) => setField("electricityComment", e.target.value)}
          />
        </Section>

        <Section
          title="Drainage comment"
          helper="Additional information about the project, particular constraints or internal notes."
        >
          <TextArea
            placeholder={t("Your comment")}
            rows={3}
            value={formData.drainageComment}
            callback={(e) => setField("drainageComment", e.target.value)}
          />
        </Section>

        <div className="d-flex justify-content-center mt-4">
          <Button
            loading={updateLoading}
            onClick={updateLoading ? undefined : handleSave}
          >
            {t("Save")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectInfo;
